/**
 * Internal Admin API client using the generated OpenAPI client.
 * Uses Bearer token auth from login; handles 401 by clearing session and redirecting.
 *
 * Note: Import from generated path to avoid bundling all API DTOs. The internal admin
 * endpoints lack security in the OpenAPI spec, so we add the token via axios interceptor.
 */

import axios, {
  type AxiosResponse,
  type AxiosError,
  type InternalAxiosRequestConfig,
} from 'axios';
import {
  InternalAdminApi,
  type InternalAdminControllerGetJobsV1StateEnum,
} from '../../../../libs/api-client/src/generated/api/internal-admin-api';
import { Configuration } from '../../../../libs/api-client/src/generated/configuration';
import type { LoginResponseDto } from '../../../../libs/api-client/src/generated/models';
import { getToken, clearToken } from './auth';

// ── Types (re-export from API response shapes; backend returns inline types) ──

export interface QueueSummary {
  name: string;
  waiting: number;
  active: number;
  completed: number;
  failed: number;
  delayed: number;
  paused: boolean;
  processingRate: number;
  failureRate: number;
}

export interface QueueHealth {
  healthy: boolean;
  issues: string[];
  totalJobs: number;
  totalWaiting: number;
  totalActive: number;
  totalFailed: number;
  avgProcessingRate: number;
  avgFailureRate: number;
}

export interface QueuesResponse {
  queues: QueueSummary[];
  health: QueueHealth;
  timestamp: string;
}

export interface JobSummary {
  id: string;
  name: string;
  state: string;
  timestamp: number;
  processedOn?: number;
  finishedOn?: number;
  attemptsMade: number;
  maxAttempts: number;
  failedReason?: string;
  dataPreview: string;
  priority?: number;
  delay?: number;
  /** Shop ID or domain from job data. */
  shopId?: string;
}

export interface JobDetail extends JobSummary {
  data: unknown;
  stacktrace: string[];
  opts: unknown;
  returnvalue?: unknown;
}

export interface JobsResponse {
  jobs: JobSummary[];
  total: number;
}

export interface MetricsSnapshot {
  timestamp: string;
  queues: Record<
    string,
    {
      waiting: number;
      active: number;
      completed: number;
      failed: number;
      delayed: number;
      processingRate: number;
      failureRate: number;
    }
  >;
}

export type JobState =
  | 'waiting'
  | 'active'
  | 'completed'
  | 'failed'
  | 'delayed';

// ── Client factory ──────────────────────────────────────────────────────────

function getApiBasePath(): string {
  const base = import.meta.env['VITE_API_BASE_URL'] ?? '';
  return base ? `${base.replace(/\/$/, '')}/api` : '/api';
}

/** The sign-in routes, whose 401 means "wrong password", not "session over". */
const AUTH_PATH = '/internal-admin/auth/';

function createAxiosWithAuthInterceptor() {
  const instance = axios.create();
  instance.interceptors.request.use((config: InternalAxiosRequestConfig) => {
    const token = getToken();
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    return config;
  });
  instance.interceptors.response.use(
    (r: AxiosResponse) => r,
    (err: AxiosError) => {
      const isLoginRequest = err.config?.url?.includes(AUTH_PATH);
      if (err.response?.status === 401 && !isLoginRequest) {
        clearToken();
        window.location.href = '/login';
      }
      throw err;
    },
  );
  return instance;
}

function getInternalAdminClient(): InternalAdminApi {
  const basePath = getApiBasePath();
  const config = new Configuration({ basePath });
  const axiosInstance = createAxiosWithAuthInterceptor();
  return new InternalAdminApi(config, basePath, axiosInstance);
}

// ── Auth ────────────────────────────────────────────────────────────────────

/**
 * Signs in. A one-time password answers `passwordChangeRequired` WITHOUT a token: the sign-in
 * screen then asks for a new password and calls {@link changePassword}, which issues it.
 */
export async function login(
  username: string,
  password: string,
): Promise<LoginResponseDto> {
  const basePath = getApiBasePath();
  const config = new Configuration({ basePath });
  const client = new InternalAdminApi(
    config,
    basePath,
    createAxiosWithAuthInterceptor(),
  );
  const { data } = await client.internalAdminControllerLoginV1({
    loginDto: { username, password },
  });
  return data;
}

/**
 * Replaces the operator's password and returns a fresh token. Every other session of the operator
 * ends — the API bumps their session version.
 */
export async function changePassword(
  username: string,
  password: string,
  newPassword: string,
): Promise<LoginResponseDto> {
  const basePath = getApiBasePath();
  const config = new Configuration({ basePath });
  const client = new InternalAdminApi(
    config,
    basePath,
    createAxiosWithAuthInterceptor(),
  );
  const { data } = await client.internalAdminControllerChangePasswordV1({
    changePasswordDto: { username, password, newPassword },
  });
  return data;
}

// ── Queue endpoints ─────────────────────────────────────────────────────────

export async function fetchQueues(): Promise<QueuesResponse> {
  const client = getInternalAdminClient();
  const { data } = await client.internalAdminControllerGetAllQueuesV1();
  return (data ?? {}) as QueuesResponse;
}

export async function fetchJobs(
  queue: string,
  state: JobState,
  page: number,
  limit: number,
  options?: { shopId?: string; search?: string },
): Promise<JobsResponse> {
  const client = getInternalAdminClient();
  const { data } = await client.internalAdminControllerGetJobsV1({
    name: queue,
    state: state as InternalAdminControllerGetJobsV1StateEnum,
    page: String(page),
    limit: String(limit),
    shopId: options?.shopId,
    search: options?.search,
  });
  return (data ?? {}) as JobsResponse;
}

export async function fetchJob(queue: string, id: string): Promise<JobDetail> {
  const client = getInternalAdminClient();
  const { data } = await client.internalAdminControllerGetJobV1({
    name: queue,
    id,
  });
  return (data ?? {}) as JobDetail;
}

// Every queue action writes an `OperatorAuditLog` row and so takes a typed reason (work order
// AC-G4c). The responses carry that row's id, which the caller shows so the operator can find
// the entry again — see `components/ReasonConfirm.tsx`.

export type {
  QueueDiscardAllResponseDto as QueueDiscardAllResponse,
  QueueJobActionResponseDto as QueueJobActionResponse,
  QueuePauseResponseDto as QueuePauseResponse,
  QueueRetryAllResponseDto as QueueRetryAllResponse,
} from '../../../../libs/api-client/src/generated/models';

import type {
  QueueDiscardAllResponseDto,
  QueueJobActionResponseDto,
  QueuePauseResponseDto,
  QueueRetryAllResponseDto,
} from '../../../../libs/api-client/src/generated/models';

/** Retries one job. Audited, best-effort. */
export async function retryJob(
  queue: string,
  id: string,
  reason: string,
): Promise<QueueJobActionResponseDto> {
  const { data } =
    await getInternalAdminClient().internalAdminControllerRetryJobV1({
      name: queue,
      id,
      operatorReasonDto: { reason },
    });
  return data;
}

/**
 * Removes one job. Audited DURABLY — the API refuses when the row cannot be written.
 *
 * A POST to `…/discard` now, not a DELETE: the reason is a body, and a body on DELETE is dropped
 * by some proxies.
 */
export async function discardJob(
  queue: string,
  id: string,
  reason: string,
): Promise<QueueJobActionResponseDto> {
  const { data } =
    await getInternalAdminClient().internalAdminControllerDiscardJobV1({
      name: queue,
      id,
      operatorReasonDto: { reason },
    });
  return data;
}

/** Retries every failed job in a queue. Audited, best-effort. */
export async function retryAll(
  queue: string,
  reason: string,
): Promise<QueueRetryAllResponseDto> {
  const { data } =
    await getInternalAdminClient().internalAdminControllerRetryAllV1({
      name: queue,
      operatorReasonDto: { reason },
    });
  return data;
}

/** Discards every failed job in a queue. Audited durably. */
export async function discardAll(
  queue: string,
  reason: string,
): Promise<QueueDiscardAllResponseDto> {
  const { data } =
    await getInternalAdminClient().internalAdminControllerDiscardAllV1({
      name: queue,
      operatorReasonDto: { reason },
    });
  return data;
}

/** Pauses a queue. Audited, best-effort — the brake has to work while Postgres is down. */
export async function pauseQueue(
  queue: string,
  reason: string,
): Promise<QueuePauseResponseDto> {
  const { data } =
    await getInternalAdminClient().internalAdminControllerPauseV1({
      name: queue,
      operatorReasonDto: { reason },
    });
  return data;
}

/** Resumes a paused queue. Audited, best-effort. */
export async function resumeQueue(
  queue: string,
  reason: string,
): Promise<QueuePauseResponseDto> {
  const { data } =
    await getInternalAdminClient().internalAdminControllerResumeV1({
      name: queue,
      operatorReasonDto: { reason },
    });
  return data;
}

// ── Metrics ────────────────────────────────────────────────────────────────

export async function fetchMetricsHistory(
  minutes: number,
): Promise<{ history: MetricsSnapshot[] }> {
  const client = getInternalAdminClient();
  const { data } = await client.internalAdminControllerGetMetricsHistoryV1({
    minutes: String(minutes),
  });
  return (data ?? {}) as { history: MetricsSnapshot[] };
}

// ── Custom system-alert broadcast (operator-only) ───────────────────────────

// Re-exported from the generated client, like the other API types, so the broadcast form
// tracks the API contract. The locale/severity/status/tier ENUMS are re-exported as values (not
// just types): they are the dashboard's source of truth for which shop languages a broadcast
// must carry copy for, so a locale added on the backend shows up as a new tab here after the
// next `npm run generate:api-client`.
export {
  BroadcastAlertDtoTypeEnum,
  BroadcastAlertTranslationDtoLocaleEnum,
  BroadcastAlertTargetDtoSubscriptionStatusesEnum,
  BroadcastAlertTargetDtoSubscriptionTiersEnum,
} from '../../../../libs/api-client/src/generated/models';

export type {
  BroadcastAlertDto as BroadcastAlertInput,
  BroadcastAlertResponseDto as BroadcastAlertResponse,
  BroadcastAlertSampleDto as BroadcastAlertSample,
  BroadcastAlertTargetDto as BroadcastAlertTarget,
  BroadcastAlertTranslationDto as BroadcastAlertTranslation,
} from '../../../../libs/api-client/src/generated/models';

import type { BroadcastAlertDto } from '../../../../libs/api-client/src/generated/models';
import type { BroadcastAlertResponseDto } from '../../../../libs/api-client/src/generated/models';

/** Calls POST /internal-admin/alerts/broadcast via the generated API client. */
export async function broadcastAlert(
  input: BroadcastAlertDto,
): Promise<BroadcastAlertResponseDto> {
  const client = getInternalAdminClient();
  const { data } = await client.broadcastAlert({
    broadcastAlertDto: input,
  });
  return data;
}
